import React from 'react';
import Container from 'react-bootstrap/Container';
import { Link } from 'react-router-dom';
import {
    ArrowClockwise,
    CheckCircleFill,
    ClockHistory,
    Grid3x3GapFill,
    InfoCircleFill,
    Tools,
} from 'react-bootstrap-icons';

import './MaintenancePage.css';

export interface MaintenancePageProps {
    serviceName?: string;
    message?: string;
    contextLabel?: string;
    showRefreshAction?: boolean;
}

function MaintenancePage({
    serviceName,
    message,
    contextLabel = 'Platform service',
    showRefreshAction = true,
}: MaintenancePageProps): React.ReactElement {
    const title = serviceName ? `${serviceName} is in maintenance` : 'Service is in maintenance';
    const detail = message || 'This service is temporarily under maintenance. Please try again later.';

    function refreshPage(): void {
        if (typeof window !== 'undefined') {
            window.location.reload();
        }
    }

    return (
        <Container fluid className="maintenance-page">
            <section className="maintenance-panel" aria-labelledby="maintenance-title">
                <div className="maintenance-layout">
                    <div className="maintenance-visual" aria-hidden="true">
                        <div className="maintenance-console">
                            <div className="maintenance-console-topline">
                                <span />
                                <span />
                                <span />
                            </div>
                            <div className="maintenance-signal">
                                <span className="maintenance-signal-ring maintenance-signal-ring-one" />
                                <span className="maintenance-signal-ring maintenance-signal-ring-two" />
                                <span className="maintenance-signal-core">
                                    <Tools size={34} />
                                </span>
                            </div>
                            <div className="maintenance-progress">
                                <span />
                            </div>
                            <div className="maintenance-node-grid">
                                <span className="is-active" />
                                <span />
                                <span />
                                <span />
                                <span className="is-paused" />
                                <span />
                                <span />
                                <span />
                                <span className="is-active" />
                            </div>
                        </div>
                        <div className="maintenance-live-strip">
                            <span className="maintenance-live-dot" />
                            Maintenance in progress
                        </div>
                    </div>

                    <div className="maintenance-content">
                        <div className="maintenance-status-row">
                            <span className="maintenance-status-pill">
                                <span className="maintenance-status-dot" />
                                Service maintenance
                            </span>
                            <span className="maintenance-context">{contextLabel}</span>
                        </div>

                        <div className="maintenance-copy">
                            <h1 id="maintenance-title">{title}</h1>
                            <p>{detail}</p>
                        </div>

                        <div className="maintenance-summary-grid">
                            <div className="maintenance-summary-item">
                                <span className="maintenance-summary-icon">
                                    <ClockHistory size={17} />
                                </span>
                                <div>
                                    <strong>Current state</strong>
                                    <span>Temporarily paused</span>
                                </div>
                            </div>
                            <div className="maintenance-summary-item">
                                <span className="maintenance-summary-icon">
                                    <InfoCircleFill size={16} />
                                </span>
                                <div>
                                    <strong>Affected service</strong>
                                    <span>{serviceName || 'Selected platform service'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="maintenance-info-grid">
                            <div className="maintenance-info-block">
                                <h2>Paused for now</h2>
                                <p>New actions for this service are blocked while the platform team completes maintenance.</p>
                            </div>
                            <div className="maintenance-info-block">
                                <h2>Still available</h2>
                                <ul>
                                    <li><CheckCircleFill size={14} /> Existing records and history</li>
                                    <li><CheckCircleFill size={14} /> Logs, evidence, and downloads</li>
                                    <li><CheckCircleFill size={14} /> Read-only status views</li>
                                </ul>
                            </div>
                        </div>

                        <div className="maintenance-actions">
                            {showRefreshAction && (
                                <button type="button" className="maintenance-refresh-btn" onClick={refreshPage}>
                                    <ArrowClockwise size={15} />
                                    Refresh status
                                </button>
                            )}
                            <Link to="/dashboard" className="maintenance-dashboard-link">
                                <Grid3x3GapFill size={14} />
                                Dashboard
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </Container>
    );
}

export default MaintenancePage;
